import { HYDRATE } from 'next-redux-wrapper'

import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'

import { StreamingType } from '@/graphql/generated/schemas'

import type { AppState } from '../store'

// A single configured cell of the video wall. Holds the device id (the id the
// streaming queries key off), plus cached display/analytics metadata so the
// grid can render and report without re-deriving from the camera list — and so
// a persisted layout still has labels after a reload.
export interface CellCameraIProps {
  deviceId: string
  name: string
  facilityName?: string
  streamingType: StreamingType
}

// Fixed 2x3 grid → 6 cells. Kept here (rather than in the feature) because it
// defines the length of the persisted `cells` array. Grid layout dimensions
// (rows/cols) live in the feature's constants.
export const VIDEO_WALL_CELL_COUNT = 6

const emptyCells = (): (CellCameraIProps | null)[] =>
  Array(VIDEO_WALL_CELL_COUNT).fill(null)

export interface VideoWallStateIProps {
  // Whether the full-screen video wall overlay is currently open. Toggled by the
  // global "W" shortcut (see useVideoWallShortcut) and readable by other surfaces
  // (e.g. the first-time-use tour) that want to open the wall or react to it.
  isOpen: boolean
  // The configured grid. Each entry is a placed cloud camera or null (empty
  // cell). Length is always VIDEO_WALL_CELL_COUNT. Persisted across reloads
  // (see the nested persistReducer in store.ts); `isOpen` is not.
  cells: (CellCameraIProps | null)[]
}

export const initialState: VideoWallStateIProps = {
  isOpen: false,
  cells: emptyCells(),
}

export const videoWallSlice = createSlice({
  name: 'videoWall',
  initialState,
  reducers: {
    openVideoWall: (state) => {
      state.isOpen = true
    },
    closeVideoWall: (state) => {
      state.isOpen = false
    },
    toggleVideoWall: (state) => {
      state.isOpen = !state.isOpen
    },
    setCameraAtCell: (
      state,
      action: PayloadAction<{ index: number; camera: CellCameraIProps }>
    ) => {
      const { index, camera } = action.payload
      if (index < 0 || index >= state.cells.length) {
        return
      }
      // Guard against the same camera occupying two cells (the picker already
      // excludes placed cameras, but stay defensive against stale/persisted UI).
      const isAlreadyPlaced = state.cells.some(
        (cell, cellIndex) =>
          cellIndex !== index && cell?.deviceId === camera.deviceId
      )
      if (isAlreadyPlaced) {
        return
      }
      state.cells[index] = camera
    },
    removeCameraAtCell: (state, action: PayloadAction<{ index: number }>) => {
      const { index } = action.payload
      if (index < 0 || index >= state.cells.length) {
        return
      }
      state.cells[index] = null
    },
    clearVideoWall: (state) => {
      state.cells = emptyCells()
    },
  },
  extraReducers: (builder) => {
    // Open-state is client-only ephemeral UI, so ignore server hydration —
    // otherwise a HYDRATE on navigation could reset the overlay unexpectedly.
    builder.addCase(HYDRATE, (state) => state)
  },
})

export const {
  openVideoWall,
  closeVideoWall,
  toggleVideoWall,
  setCameraAtCell,
  removeCameraAtCell,
  clearVideoWall,
} = videoWallSlice.actions

export const selectVideoWallIsOpen = (state: AppState): boolean =>
  state.videoWall.isOpen

export const selectVideoWallCells = (
  state: AppState
): (CellCameraIProps | null)[] => state.videoWall.cells

export const selectVideoWallCameraCount = (state: AppState): number =>
  state.videoWall.cells.filter(Boolean).length

// Memoized: returns a new array. Without createSelector every render would
// get a fresh reference and trigger redundant re-renders (Redux Toolkit warns
// about this).
export const selectPlacedDeviceIds = createSelector(
  [selectVideoWallCells],
  (cells): string[] =>
    cells
      .filter((cell): cell is CellCameraIProps => cell !== null)
      .map((cell) => cell.deviceId)
)

// Default export for reducers is a pattern recommended by Redux
// eslint-disable-next-line import/no-default-export
export default videoWallSlice.reducer
